import axios from "axios";
import { useEffect, useState } from "react";
import { Loader2, X, Trash2, Plus, Minus, Search } from "lucide-react";
import { showError, showSuccess } from "../../../utils/toastUtils";

const AddServiceToBookingModal = ({
  isOpen,
  onClose,
  booking,
  refreshBooking,
}) => {
  const [services, setServices] = useState([]);
  const [existingServices, setExistingServices] = useState([]);
  const [newSelected, setNewSelected] = useState([]);
  const [search, setSearch] = useState("");
  const [finalAmountInput, setFinalAmountInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetchingServices, setFetchingServices] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setExistingServices(
      (booking?.services ?? []).map((s) => ({
        serviceId: s.serviceId._id || s.serviceId,
        name: s.serviceId?.name || "Service",
        price: s.finalPrice ?? 0,
        brand: s.brand ?? null,
        qty: s.qty ?? 1,
      }))
    );
    setNewSelected([]);
    setFinalAmountInput("");
    setSearch("");
    fetchServices();
  }, [isOpen]);

  const fetchServices = async () => {
    setFetchingServices(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/services/get-all`
      );
      setServices(res.data.data);
    } catch {
      showError("Failed to fetch services");
    } finally {
      setFetchingServices(false);
    }
  };

  const filteredServices = services.filter((s) =>
    s.name.toLowerCase().includes(search.toLowerCase())
  );

  const addService = (service) => {
    const existsNew = newSelected.find((s) => s.serviceId === service._id);
    if (existsNew) {
      setNewSelected((prev) =>
        prev.map((s) =>
          s.serviceId === service._id ? { ...s, qty: s.qty + 1 } : s
        )
      );
      return;
    }
    const isMulti = Array.isArray(service.type) && service.type.length > 0;
    const price = isMulti ? service.type[0]?.discountedPrice : service.discountedPrice;
    const brand = isMulti ? service.type[0]?.brand : null;
    setNewSelected((prev) => [
      ...prev,
      { serviceId: service._id, name: service.name, price: price ?? 0, brand, qty: 1 },
    ]);
  };

  const updateNewQty = (index, type) => {
    setNewSelected((prev) =>
      prev.map((s, i) => {
        if (i !== index) return s;
        if (type === "inc") return { ...s, qty: s.qty + 1 };
        if (type === "dec" && s.qty > 1) return { ...s, qty: s.qty - 1 };
        return s;
      })
    );
  };

  const removeNewService = (index) => {
    setNewSelected((prev) => prev.filter((_, i) => i !== index));
  };

  const updateExistingQty = (index, type) => {
    setExistingServices((prev) =>
      prev.map((s, i) => {
        if (i !== index) return s;
        if (type === "inc") return { ...s, qty: s.qty + 1 };
        if (type === "dec" && s.qty > 1) return { ...s, qty: s.qty - 1 };
        return s;
      })
    );
  };

  const removeExistingService = (index) => {
    if (existingServices.length + newSelected.length <= 1) {
      return showError("At least one service must remain on the booking");
    }
    setExistingServices((prev) => prev.filter((_, i) => i !== index));
  };

  const existingTotal = existingServices.reduce((sum, s) => sum + (s.price ?? 0) * s.qty, 0);
  const newServicesTotal = newSelected.reduce((sum, s) => sum + (s.price ?? 0) * s.qty, 0);
  const subtotal = existingTotal + newServicesTotal;

  const handleSave = async () => {
    const totalServices = existingServices.length + newSelected.length;
    if (totalServices === 0) return showError("At least one service is required");
    setLoading(true);
    try {
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/service-bookings/update-services/${booking._id}`,
        {
          services: [
            ...existingServices.map((s) => ({ serviceId: s.serviceId, brand: s.brand, qty: s.qty })),
            ...newSelected.map((s) => ({ serviceId: s.serviceId, brand: s.brand, qty: s.qty })),
          ]
        }
      );
      showSuccess("Booking updated successfully");
      refreshBooking();
      onClose();
    } catch (err) {
      showError(err.response?.data?.message || "Failed to save");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-2xl rounded-t-3xl sm:rounded-2xl flex flex-col max-h-[92vh] sm:max-h-[90vh]">

        {/* HEADER */}
        <div className="flex justify-between items-center px-5 py-4 border-b border-gray-100">
          <div>
            <h2 className="font-bold text-base text-gray-900">Manage Services</h2>
            <p className="text-xs text-gray-400 mt-0.5">Add or remove services from this booking</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-500">
            <X size={16} />
          </button>
        </div>

        {/* BODY */}
        <div className="flex flex-col flex-1 overflow-hidden">
          <div className="p-4 space-y-4 overflow-y-auto flex-1">

            {/* EXISTING SERVICES */}
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Current Services ({existingServices.length})
              </p>
              {existingServices.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-3 border border-dashed border-gray-200 rounded-xl">
                  All services removed — add at least one below
                </p>
              ) : (
                <div className="space-y-2">
                  {existingServices.map((s, i) => (
                    <div key={`existing-${i}`} className="flex items-center justify-between bg-orange-50 border border-orange-100 px-3 py-2.5 rounded-xl">
                      <div className="flex-1 min-w-0 mr-3">
                        <p className="text-sm font-semibold text-gray-800 truncate">{s.name}</p>
                        <p className="text-xs text-gray-400">
                          ₹{s.price} × {s.qty} = <span className="font-medium text-gray-600">₹{(s.price * s.qty).toFixed(2)}</span>
                          {s.brand && <span className="ml-1 text-gray-400">· {s.brand}</span>}
                        </p>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <button onClick={() => updateExistingQty(i, "dec")} className="w-6 h-6 border border-gray-200 rounded-lg flex items-center justify-center bg-white">
                          <Minus size={11} />
                        </button>
                        <span className="text-sm font-semibold w-5 text-center">{s.qty}</span>
                        <button onClick={() => updateExistingQty(i, "inc")} className="w-6 h-6 border border-gray-200 rounded-lg flex items-center justify-center bg-white">
                          <Plus size={11} />
                        </button>
                        <button onClick={() => removeExistingService(i)} className="w-6 h-6 flex items-center justify-center text-red-400 ml-1" title="Remove service">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="border-t border-dashed border-gray-200" />

            {/* ADD NEW SERVICES */}
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Add New Services</p>

              <div className="relative mb-2">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  placeholder="Search service..."
                  className="w-full border border-gray-200 rounded-xl pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-primaryBtnColor/30 focus:border-primaryBtnColor"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              <div className="border-brandAccentColor border rounded-xl overflow-hidden bg-gray-50">
                {fetchingServices ? (
                  <div className="flex justify-center py-6"><Loader2 size={20} className="animate-spin text-gray-400" /></div>
                ) : filteredServices.length === 0 ? (
                  <p className="text-center text-sm text-gray-400 py-6">No services found</p>
                ) : (
                  <div className="max-h-40 overflow-y-auto divide-y divide-gray-100">
                    {filteredServices.map((s) => {
                      const isMulti = Array.isArray(s.type) && s.type.length > 0;
                      const price = isMulti ? s.type[0]?.discountedPrice : s.discountedPrice;
                      const isInNew = newSelected.some((sel) => sel.serviceId === s._id);
                      const isInExisting = existingServices.some((ex) => ex.serviceId === s._id);
                      return (
                        <div
                          key={s._id}
                          onClick={() => addService(s)}
                          className={`flex items-center justify-between cursor-pointer px-3 py-2.5 hover:bg-white transition-colors ${isInNew || isInExisting ? "bg-white" : ""}`}
                        >
                          <div>
                            <p className="text-sm font-medium text-gray-800">{s.name}</p>
                            {isMulti && <p className="text-xs text-gray-400">{s.type[0]?.brand}</p>}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-gray-700">₹{price ?? "—"}</span>
                            {isInExisting ? (
                              <span className="text-[10px] bg-orange-100 text-orange-600 px-1.5 py-0.5 rounded-full font-medium">Existing</span>
                            ) : isInNew ? (
                              <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-medium">Added</span>
                            ) : (
                              <span className="w-5 h-5 rounded-full border border-gray-300 flex items-center justify-center">
                                <Plus size={11} className="text-gray-400" />
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {newSelected.length > 0 && (
                <div className="mt-3 space-y-2">
                  {newSelected.map((s, i) => (
                    <div key={`new-${i}`} className="flex items-center justify-between bg-green-50 border border-green-100 px-3 py-2.5 rounded-xl">
                      <div className="flex-1 min-w-0 mr-3">
                        <div className="flex items-center gap-1.5">
                          <p className="text-sm font-semibold text-gray-800 truncate">{s.name}</p>
                          <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-medium shrink-0">New</span>
                        </div>
                        <p className="text-xs text-gray-400">
                          ₹{s.price} × {s.qty} = <span className="font-medium text-gray-600">₹{(s.price * s.qty).toFixed(2)}</span>
                        </p>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <button onClick={() => updateNewQty(i, "dec")} className="w-6 h-6 border border-gray-200 rounded-lg flex items-center justify-center bg-white">
                          <Minus size={11} />
                        </button>
                        <span className="text-sm font-semibold w-5 text-center">{s.qty}</span>
                        <button onClick={() => updateNewQty(i, "inc")} className="w-6 h-6 border border-gray-200 rounded-lg flex items-center justify-center bg-white">
                          <Plus size={11} />
                        </button>
                        <button onClick={() => removeNewService(i)} className="w-6 h-6 flex items-center justify-center text-red-400 ml-1">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* FOOTER */}
          <div className="px-4 py-3 border-t border-gray-100">
            <button
              onClick={handleSave}
              disabled={loading}
              className="w-full bg-primaryBtnColor hover:bg-primaryBtnHoverColor text-white rounded-xl py-3 font-semibold text-sm flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
            >
              {loading ? (
                <><Loader2 className="animate-spin" size={16} />Saving…</>
              ) : (
                "Save Changes"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddServiceToBookingModal;